import { createCookieName } from '@app/utils/cookieNames'
import { createCookie } from '@app/utils/cookies'
import { createSelector } from '@app/utils/createSelector'
import { jsonSchemeParse } from '@app/utils/jsonSchemeParse'
import moment from '@app/utils/moment'
import { valita } from '@app/utils/valita'

import type { ApiEmailToken } from '@app/packages/api/v2/types/entities'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'

import { getCookies } from '@app/store/actions/contexts/cookies'
import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

export type AcademyProfile = { email: string; token: string } | null

// undefined = not yet restored from the cookie; null = restored, no session.
const academyProfileSlice = createReduxSlice<AcademyProfile>('academy_profile')

export const logInAcademyUser = (token: ApiEmailToken) =>
  createThunk(async dispatch => {
    const cookies = dispatch(getCookies())
    const profile = { email: token.attributes.email, token: token.attributes.value }
    await academySessionCookie.set(cookies, profile)
    dispatch(academyProfileSlice.set(profile))
  })

export const destroyAcademyUser = () =>
  createThunk(async dispatch => {
    const cookies = dispatch(getCookies())
    await academySessionCookie.rm(cookies)
    dispatch(academyProfileSlice.set(null))
  })

export const academyUserSelector = createSelector([academyProfileSlice.selector], profile => profile ?? null)

// Eagerly restores the academy session from its cookie the first time it's read (resolve it in
// fetchData); replaces the old imperative restoreAcademyUser.
export const academyUserResultSelector = createSelector([academyProfileSlice.selector], profile =>
  createLoadable(c => {
    if (profile === undefined)
      return c.load(
        createPromise('Academy profile must be restored', 'cookie', async dispatch => {
          const cookies = dispatch(getCookies())
          dispatch(academyProfileSlice.set(await academySessionCookie.get(cookies)))
        })
      )
    return profile
  })
)

const academySessionCookie = createCookie<AcademyProfile>(
  createCookieName('kidsout__academy_session'),
  input => jsonSchemeParse(input, valita.object({ email: valita.string(), token: valita.string() })),
  input => JSON.stringify(input),
  () => ({ expires: moment().add(1, 'year') })
)
